import React, { useEffect, useState } from 'react';
import { PhoneIcon, MailIcon, MenuIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WhatsAppIcon } from './WhatsAppIcon';
interface NavbarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}
export function Navbar({
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'hypotheken',
    label: 'Hypotheken'
  }, {
    id: 'woonboten',
    label: 'Woonboten'
  }, {
    id: 'verzekeringen',
    label: 'Verzekeringen'
  }, {
    id: 'over-ons',
    label: 'Over ons'
  }, {
    id: 'kosten',
    label: 'Kosten'
  }, {
    id: 'contact',
    label: 'Contact'
  }];
  const handleNavClick = (id: string) => {
    onNavigate(id);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };
  return <header className="fixed top-0 left-0 right-0 z-50 flex flex-col" data-id="element-109">
      {/* Top Bar */}
      <div className="bg-navy text-white py-2 px-4 md:px-8 text-xs md:text-sm font-medium" data-id="element-110">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-111">
          <div className="flex items-center space-x-6" data-id="element-112">
            <a href="tel:0201234567" className="flex items-center hover:text-gold transition-colors" data-id="element-113">
              <PhoneIcon className="w-3 h-3 md:w-4 md:h-4 mr-2" data-id="element-114" />
              020-1234567
            </a>
            <a href="mailto:info@jdg-advies.nl" className="hidden md:flex items-center hover:text-gold transition-colors" data-id="element-115">
              <MailIcon className="w-4 h-4 mr-2" data-id="element-116" />
              info@jdg-advies.nl
            </a>
          </div>
          <a href="https://wa.me/31612345678" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-gold transition-colors" data-id="element-117">
            <WhatsAppIcon className="w-4 h-4 mr-2" data-id="element-118" />
            <span className="hidden md:inline" data-id="element-119">WhatsApp ons</span>
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <div className={`transition-all duration-300 ${isScrolled ? 'navbar-glass py-3' : 'bg-white py-5'}`} data-id="element-120">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center" data-id="element-121">
          {/* Logo */}
          <div className="cursor-pointer flex-shrink-0" onClick={() => handleNavClick('home')} data-id="element-122">
            <img src="https://cdn.magicpatterns.com/uploads/iq7SCoyHFduQKkxbRqiiPv/JDG_logo.jpg" alt="JDG Hypotheken | Verzekeringen" className="h-10 md:h-14 object-contain" data-id="element-123" />
          </div>

          {/* Desktop Links */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8" data-id="element-124">
            {navLinks.map(link => <button key={link.id} onClick={() => handleNavClick(link.id)} className={`nav-link text-sm xl:text-base ${currentPage === link.id ? 'active' : ''}`} data-id="element-125">
                {link.label}
              </button>)}
          </nav>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center space-x-4" data-id="element-126">
            <a href="tel:0201234567" className="hidden md:flex items-center bg-gold hover:bg-gold-dark text-white px-5 py-2.5 rounded-brand font-semibold transition-colors shadow-sm" data-id="element-127">
              <PhoneIcon className="w-4 h-4 mr-2" data-id="element-128" />
              020-1234567
            </a>
            <button className="lg:hidden text-navy p-2" onClick={() => setIsMobileMenuOpen(true)} aria-label="Menu openen" data-id="element-129">
              <MenuIcon className="w-6 h-6" data-id="element-130" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-131">
        {isMobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-[60] lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-132" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-4/5 max-w-sm bg-white z-[70] shadow-2xl flex flex-col lg:hidden" data-id="element-133">
              <div className="p-6 flex justify-between items-center border-b border-gray-100" data-id="element-134">
                <img src="https://cdn.magicpatterns.com/uploads/iq7SCoyHFduQKkxbRqiiPv/JDG_logo.jpg" alt="JDG Logo" className="h-8 object-contain" data-id="element-135" />
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-500 hover:text-navy bg-gray-50 rounded-full" aria-label="Menu sluiten" data-id="element-136">
                  <XIcon className="w-5 h-5" data-id="element-137" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-6 px-6 flex flex-col space-y-6" data-id="element-138">
                {navLinks.map(link => <button key={link.id} onClick={() => handleNavClick(link.id)} className={`text-left text-lg font-medium py-2 border-b border-gray-50 ${currentPage === link.id ? 'text-gold' : 'text-navy'}`} data-id="element-139">
                    {link.label}
                  </button>)}
              </div>
              <div className="p-6 border-t border-gray-100 bg-warm" data-id="element-140">
                <p className="text-sm text-gray-500 mb-4" data-id="element-141">
                  Direct contact opnemen?
                </p>
                <a href="tel:0201234567" className="flex items-center justify-center w-full bg-navy text-white py-3 rounded-brand font-medium mb-3" data-id="element-142">
                  <PhoneIcon className="w-4 h-4 mr-2" data-id="element-143" />
                  Bel 020-1234567
                </a>
                <a href="mailto:info@jdg-advies.nl" className="flex items-center justify-center w-full bg-white border border-gray-200 text-navy py-3 rounded-brand font-medium" data-id="element-144">
                  <MailIcon className="w-4 h-4 mr-2" data-id="element-145" />
                  Mail ons
                </a>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}